/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import React from 'react';
import { i18n } from 'shared/i18n';
import UDText from 'ud-ui/components/text';
import IconBadge, { AnalyticsTone, TONE_COLORS } from './icon-badge';

interface KpiCardProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  tone?: AnalyticsTone;
  /** Доля от целого, 0..1: под значением рисуется полоса прогресса. */
  progress?: number;
  /** Пояснение к полосе, например «из 29». */
  progressHint?: string;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceCard};
  border: 1px solid ${({ theme }) => theme.colors.LineSubtle};
  border-radius: ${({ theme }) => theme.radii.panel};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevation.low};
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Track = styled.div`
  height: 6px;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.SurfaceSunken};
  overflow: hidden;
`;

const Fill = styled.div<{ $color: string; $ratio: number }>`
  height: 100%;
  width: ${({ $ratio }) => Math.round($ratio * 100)}%;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ $color }) => $color};
  transition: width 0.4s ease;
`;

function formatValue(value: string | number): string {
  if (typeof value === 'string') return value;
  if (Number.isInteger(value)) {
    return new Intl.NumberFormat(i18n.locale).format(value);
  }
  return value.toFixed(2);
}

const KpiCard = React.memo(({ icon, value, label, tone = 'gold', progress, progressHint }: KpiCardProps) => {
  const hasProgress = progress !== undefined;

  return (
    <Card>
      <Top>
        <IconBadge icon={icon} tone={tone} />
        <Texts>
          <UDText variant="title-1" as="div" className="!text-inkPrimary leading-none">
            {formatValue(value)}
          </UDText>
          <UDText variant="body-2" as="div" className="!text-inkSecondary">
            {label}
          </UDText>
        </Texts>
      </Top>
      {hasProgress && (
        <div className="flex flex-col gap-1">
          <Track role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
            <Fill $color={TONE_COLORS[tone]} $ratio={Math.min(Math.max(progress, 0), 1)} />
          </Track>
          {progressHint && (
            <UDText variant="text-2" as="div" className="!text-inkMuted">
              {progressHint}
            </UDText>
          )}
        </div>
      )}
    </Card>
  );
});

export default KpiCard;
